
import otp from "../components/OTPComponent.vue";
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonPage,
  IonRouterOutlet,
  IonTabs,
  IonTabBar,
  IonTabButton,
  IonTitle,
  IonToolbar,
  IonSelectOption, IonSelect, toastController
} from '@ionic/vue';
import { sync, layers, people, logOut, person, cube } from 'ionicons/icons';
import {useRoute, useRouter} from "vue-router";
import {defineComponent} from "vue";
import { Preferences } from '@capacitor/preferences';
import AuthService from "@/services/AuthService";
import validator from "email-validator";
import type { User } from "@/types/User";
import UserService from "@/services/UserService";
import UtilityService from "@/services/UtilityService";
import SessionService from "@/services/SessionService";
import {EntityAuthorization} from "@/types/EntityAuthorization";

export default defineComponent({
  name: 'AuthenticatedTabs',
  data: () => {
    return {
      prefs: {} as any,
      authenticated: false,
      uuid: useRoute().params.id as string,
      finalRoute: '',
      currentRoute : useRouter().currentRoute.value.name as string,
      user: {} as User,
      canManageUsers: false,
      editAccessVisible: false,
      editors: [] as EntityAuthorization[],
      viewers: [] as EntityAuthorization[],
      newViewerEmail: '',
      newEditorEmail: '',
      loginModalVisible: false as boolean,
      searchModalVisible: false as boolean,
      otpModalVisible: false as boolean,
      searchResults: [] as any[],
      currentEmail: "" as string,
      currentUserPhone: "" as string,
      currentUserFirstName: "" as string,
      currentUserLastName: "" as string,
      currentUserPreferredComm: '0',
      editUserVisible: false,
      digits: [],
      digitCount: 6,
      otpCount: 0,
      isToastOpen: false,
      createUser: false,
      toastMessage: "" as string,
      otpValue: '' as string,
      authenticating: false as boolean,
    };
  },
  components: {  IonButton, IonButtons, IonCard, IonCardHeader, IonCardContent, IonContent, IonHeader, IonLabel, IonSelect, IonSelectOption, IonTabs, IonTabBar, IonTabButton, IonIcon, IonInput, IonItem, IonModal, IonPage, IonRouterOutlet, IonTitle, IonToolbar, otp },

  mounted(){
    this.finalRoute = this.currentRoute;
    if (this.currentRoute.includes('/')){
      this.finalRoute = this.currentRoute.split('/')[0]
    }
    AuthService.isAuthenticated().then( (response) => {
      this.authenticated = response;
      if(!this.authenticated){
        this.loginModalVisible = true;
      } else {
        Preferences.get({ key: 'user' }).then((user: any) => {
          this.user = JSON.parse(user.value) as User;
          if(this.user.email){
            AuthService.canEmailEditEntity( this.user.email,this.uuid, this.finalRoute, (error: any) => {
              if(error){
                this.canManageUsers = false;
              } else {
                // this would error out with 403 error if we could not edit
                this.canManageUsers = true;
              }
            });
          }
        });
      }
    }).catch( (exc) => {
      console.log(`Error checking if authenticated: ${exc}`);
    });
  },
  setup() {
    const router = useRouter();
    return {
      layers,
      sync,
      person,
      people,
      logOut,
      cube,
      router
    }
  },
  watch: {
    // Auto-submit the moment the one-time code is complete — tapping Confirm
    // is still there as a fallback. Guarded so a retry after a bad code
    // (user edits a digit) submits again, but a single fill submits once.
    otpValue(value: string) {
      if (value.length === 6 && !this.authenticating) {
        this.authenticate();
      }
    }
  },
  computed: {
    // True on /a/deliveries and /a/ship/* — the Delivery Photos feature.
    isDeliveryPhotosRoute(): boolean {
      const path = this.$route?.path || '';
      return path.startsWith('/a/deliveries') || path.startsWith('/a/ship');
    }
  },
  methods: {
    removeUser(id: string){
      AuthService.removeUserFromEntity( id, (error: any, results: any) => {
        if(error){
          toastController.create({ duration: 5000, color: 'warning', message: `Error removing authorization.`, position: 'top' }).then(toast => toast.present());
        }
        if(results){
          this.loadViewersAndUsers();
        }
      });

      console.log(`removeViewer(${id})`)
    },
    addViewer(){
      if(!validator.validate(this.newViewerEmail)){
        toastController.create({ duration: 5000, color: 'warning', message: `${this.newViewerEmail} is not a valid email address.`, position: 'top' }).then(toast => toast.present());
      } else {
        AuthService.addViewerForEntity( this.finalRoute,'*', this.newViewerEmail, (error: any, results: any) => {
          if(error){
            toastController.create({ duration: 5000, color: 'warning', message: `Error creating authorization for ${this.newViewerEmail}`, position: 'top' }).then(toast => toast.present());
          }
          if(results){
            this.newViewerEmail = '';
            this.loadViewers();
          }
        });
      }
    },
    addEditor(){
      if(!validator.validate(this.newEditorEmail)){
        toastController.create({ duration: 5000, color: 'warning', message: `${this.newEditorEmail} is not a valid email address.`, position: 'top' }).then(toast => toast.present());
      } else {
        AuthService.addEditorForEntity( this.finalRoute,'*', this.newEditorEmail, (error: any, results: any) => {
          if(error){
            toastController.create({ duration: 5000, color: 'warning', message: `Error creating authorization for ${this.newEditorEmail}`, position: 'top' }).then(toast => toast.present());
          }
          if(results){
            this.newEditorEmail = '';
            this.loadEditors();
          }
        });
      }
    },
    manageUsers(){
      this.loadViewersAndUsers();
      //this.editAccessVisible = true;
    },
    loadViewersAndUsers(){
      this.loadViewers();
      this.loadEditors();
    },
    updateUser(){
      if (!this.currentUserPhone || UtilityService.validatePhone(this.currentUserPhone)) {
        // if for some reason a bad phone number made it this far, we'll just set email to default pref
        if(this.currentUserPhone == ''){
          this.currentUserPhone = '';
          this.currentUserPreferredComm = '0';
        }
        this.currentUserPhone = UtilityService.stripAllButNumbers(this.currentUserPhone)
        console.log(`Received phone number from form and am about to submit it to the service: ${this.currentUserPhone}`)
        const requestArgs = {
          id: this.user.id,
          first_name: this.currentUserFirstName,
          last_name: this.currentUserLastName,
          phone_number: this.currentUserPhone,
          email: this.user.email,
          preferred_comm: Number(this.currentUserPreferredComm)
        } as unknown as User
        UserService.update(requestArgs).then( (user: User) => {
          Preferences.set({key: 'user', value: JSON.stringify(user)}).then(() => {
            console.log(`Setting user in session storage with phone number: ${user.phone_number}`)
            toastController.create({ duration: 5000, color: 'success', message: `Preferences updated.`, position: 'top' }).then(toast => toast.present());
            this.editUserVisible = false;
          });
        }).catch( () => {
          toastController.create({ duration: 5000, color: 'warning', message: `Error updating profile or no changes were detected.`, position: 'top' }).then(toast => toast.present());
        });
      } else {
        console.log("INVALID TRYING TO SHOW TOAST")
        console.log(this.currentUserPhone == '')
        console.log(!this.currentUserPhone)
        console.log(this.currentUserPhone)
        toastController.create({ duration: 5000, color: 'warning', message: 'Phone number should contain ten numbers including the 1 + area code. Phone number can be empty or must be valid.', position: 'top' }).then(toast => toast.present());
      }
    },
    loadUserFromSessionStorage(){
      Preferences.get({ key: 'user' }).then((user: any) => {
        this.user = JSON.parse(user.value) as User;
        this.currentEmail = this.user.email;
        this.currentUserFirstName = this.user.first_name;
        this.currentUserLastName = this.user.last_name;
        this.currentUserPhone = this.user.phone_number;
        this.currentUserPreferredComm = this.user.preferred_comm.toString();
        console.log(`loadUserFromSessionStorage() this.user.phone_number: ${this.user.phone_number}`)
        console.log(`loadUserFromSessionStorage() this.currentUserPhone: ${this.currentUserPhone}`)
        //alert('about to push router')
      });
    },
    cancelUpdateUsers(){
      this.editAccessVisible = false;
    },
    cancelUpdateSelf(){
      this.editUserVisible = false;
    },
    logOff(){
      SessionService.logOut();
      this.router.go(0);
    },
    loadViewers(){
      AuthService.getViewersForEntity( this.finalRoute,'*',  (error: any, results: any) => {
        if(error){
          toastController.create({ duration: 5000, color: 'warning', message: 'Error getting viewers for current entity', position: 'top' }).then(toast => toast.present());
        }
        if(results){
          console.log(results)
          this.viewers = results as EntityAuthorization[]
        }
      });
    },
    loadEditors(){
      AuthService.getEditorsForEntity( this.finalRoute,'*',  (error: any, results: any) => {
        if(error){
          toastController.create({ duration: 5000, color: 'warning', message: 'Error getting editors for current entity', position: 'top' }).then(toast => toast.present());
        }
        if(results){
          console.log(results)
          this.editors = results as EntityAuthorization[]
        }
      });
    },
    showUserRegistration(){
      this.createUser = true;
    },
    openUserPrefs(){
      this.loadUserFromSessionStorage();
      console.log(`About to open user prefs. Here is the user phone number in session: ${this.user.phone_number}`)
      console.log(`About to open user prefs. Here is the user phone number in this.currentUserPhone: ${this.currentUserPhone}`)
      this.editUserVisible = true;
    },
    submitNewUserAndAuthenticate(){
      this.currentUserPhone = UtilityService.stripAllButNumbers(this.currentUserPhone);
      const validationErrors = [] as string[];
      if(!this.currentEmail || this.currentEmail === ''){
        validationErrors.push("Email address is required.")
      }
      if(this.currentEmail && !validator.validate(this.currentEmail)){
        validationErrors.push(`${this.currentEmail} is not a valid email address.`)
      }
      if(this.currentUserPhone && !UtilityService.validatePhone(this.currentUserPhone)){
        this.currentUserPhone = UtilityService.formatPhoneNumber(this.currentUserPhone);
        validationErrors.push(`${this.currentUserPhone} is not a valid phone number.`)
      }
      if(validationErrors.length > 0){
        validationErrors.forEach( theErrorMessage => {
          toastController.create({ duration: 5000, color: 'warning', message: theErrorMessage, position: 'top' }).then(toast => toast.present());
        });
      } else {
        console.log(`Will attempt to submit the new user.`)
        const user = {
          first_name: this.currentUserFirstName,
          last_name: this.currentUserLastName,
          phone_number: this.currentUserPhone,
          preferred_comm: this.currentUserPreferredComm,
          email: this.currentEmail
        } as unknown as User;
        AuthService.findOrCreateUserAndAuthenticateWithOTP(user, (error: any, responseUser: User) => {
          if(responseUser) {
            this.user = responseUser;
            this.loginModalVisible = false;
            this.otpModalVisible = true;
            console.log(`Success from findOrCreateUserAndAuthenticateWithOTP: ${JSON.stringify(responseUser)}`);
          }
          if(error){
            toastController.create({ duration: 5000, color: 'warning', message: 'Could not find or register that email address. Please try a different name and email address or try again later.', position: 'top' }).then(toast => toast.present());
            console.log(`Error from findOrCreateUserAndAuthenticateWithOTP: ${error}`);
          }
        });
      }
    },
    submitEmailAddress(){
      if(this.currentEmail == ''){
        const toastMessage = `Email cannot be blank.`;
        toastController.create({ duration: 5000, color: 'warning', message: toastMessage, position: 'top' }).then(toast => toast.present());
      } else if( !validator.validate(this.currentEmail) ){
        const toastMessage = `${this.currentEmail} is not a valid email.`;
        toastController.create({ duration: 5000, color: 'warning', message: toastMessage, position: 'top' }).then(toast => toast.present());
      } else {
        AuthService.canEmailViewEntity( this.currentEmail,this.uuid, this.finalRoute, (error: any, result: any) => {
          if(error){
            toastController.create({ duration: 5000, color: 'warning', message: 'You are not authorized to view this page. If you believe this is in error please contact a manager at your company or support@oscocontrols.com', position: 'top' }).then(toast => toast.present());
          } else {
            console.log(result);
            AuthService.generateOTP( this.currentEmail, (err: any, res: any) => {
              if( res ) {
                this.loginModalVisible = false;
                this.otpModalVisible = true;
                //this.$refs.otp_input.focus();
              } else if ( err ) {
                toastController.create({ duration: 5000, color: 'warning', message: 'Issue generating one time password for user. If you believe this is in error please contact a manager at your company or support@oscocontrols.com', position: 'top' }).then(toast => toast.present());
              }
            });
          }
        });
      }
    },
    authenticate(){
      if (this.authenticating) return;
      this.authenticating = true;

      AuthService.authenticate( this.currentEmail, this.otpValue, (err: any, res: any) => {
        if( res ) {
          //console.log(`Response received from authenticate: ${JSON.stringify(res)}`);
          AuthService.isAuthenticated().then( (response) => {
            this.authenticated = response;
            if(!this.authenticated){
              this.authenticating = false;
              this.loginModalVisible = true;
            } else {
              this.loadUserFromSessionStorage();
              this.loginModalVisible = false;
              this.otpModalVisible = false;
              this.router.go(0)
            }
          }).catch( () => {
            this.authenticating = false;
            toastController.create({ duration: 5000, color: 'warning', message: 'Error authenticating. Your one time passcode might have expired or there may be a system issue. If you believe this is in error please contact support@oscocontrols.com', position: 'top' }).then(toast => toast.present());
            //console.log(`Error checking if authenticated: ${exc}`);
          });
        } else if (err) {
          this.authenticating = false;
          toastController.create({ duration: 5000, color: 'warning', message: 'Error authenticating. Your one time passcode might have expired or there may be a system issue. If you believe this is in error please contact support@oscocontrols.com', position: 'top' }).then(toast => toast.present());
        }
      });
    },
    goToDelivery(){
      // Get the delivery UUID from preferences (set by DeliveryKit.vue)
      Preferences.get({ key: 'delivery_uuid' }).then((result) => {
        if(result.value != null){
          const deliveryUuid = JSON.parse(result.value);
          console.log(`Retrieved delivery_uuid from preferences: ${deliveryUuid}`);
          console.log(`Navigating to delivery: ${deliveryUuid}`);
          // Use the unauthenticated route since there's no authenticated delivery route
          this.router.push(`/c/d/${deliveryUuid}`);
        } else {
          console.log('No delivery_uuid found in preferences');
          toastController.create({ duration: 5000, color: 'warning', message: 'No delivery found. Please navigate to a delivery first.', position: 'top' }).then(toast => toast.present());
        }
      }).catch((error) => {
        console.error('Error getting delivery UUID from preferences:', error);
        toastController.create({ duration: 5000, color: 'warning', message: 'Error navigating to delivery.', position: 'top' }).then(toast => toast.present());
      });
    },
  }
});
